/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import TQFont, { tqtypes, TQFontAggregation, TQFontFamily, TQLogLevel } from "./TQTypes";
import TQInitParams from "./TQInitParam";
import TQTaskScheduler from "../controller/TQTaskScheduler";
import { TQUtils } from "../utils/TQUtils";
import { TQByofFileValidatorResult } from "./TQByofFileValidator";
import { TQCollabFontsHandler } from "../controller/TQCollabFontsHandler";
/**
 * The entry interface class for the clients to call and utlize TQWeb APIs
 * The constructor function of class TQWeb initializes all the required params for Adobe Fonts communication.(refer to class TQInitParams for all available initialization params).
 * Once Initialization is successful, the application will be notified of the successful status of the initialization via statusUpdateCallback.
 * Please refer https://git.corp.adobe.com/coretech/TypeQuestWeb/blob/main/demo/index.ts for the Demo example
 *
 * TQWeb can be constructed with TQInitParams. TQInitParams is dependent on further configurable parameters. Please check TQInitParams class further for details.
 * Such as -
 * 1. tqtypes.AuthParams which consists of userId, accessToken, appId, and if Staging env or prod. Now, userId, accessToken can be supplied with the help of updateAuthParams() method.
 * 2. statusUpdateCallback => Update callback of status. If this returns AUTH_FAILURE, clients must call updateAuthParams().
 * 3. runtimeMode => This can be ALL or only FOUND_FONTS. By default this is ALL.
 * 4. appEntitlement => If application if Freemium / Paid. By default this is tqtypes.AppEntitlement.FREEMIUM;
 *
 * Once constructed, to start the TQWeb, call .start() function OR updateAuthParams() with the correct auth parameters.
 */
export default class TQWeb {
    // Privates and private methods

    /** Task scheduler for scheduling tasks */
    private _scheduler: TQTaskScheduler;

    /** Retry Count for the scheduled tasks */
    private _retryCount = 3;

    /** Sync call for getting AF token */
    private async initToken() {
        const tokenContext: tqtypes.GenericContext<[]> = {
            retryCount: this._retryCount,
            root: [],
        };
        this._scheduler.startTimers();
        await this._scheduler.scheduleTask(tqtypes.TaskType.TOKEN_FETCH, tokenContext);
        await this._scheduler.handleCacheOnInit();
        if (this._scheduler.initParams.runtimeMode == tqtypes.RuntimeMode.FOUND_FONTS) {
            if (TQUtils.isValidAccessToken(this._scheduler.initParams.authParams.accessToken) == true) {
                this._fetchFreshCollection(tqtypes.TaskType.ACTIVATED_FONTS_COUNT);
                await this._fetchFreshCollection(tqtypes.TaskType.BYOF_COUNT);
            }
            await this._scheduler.fetchAndprocessCustomCatalog();
        }
    }

    /**
     * This function takes a promise resolver Map of TQFontIdentifiers and Promise resolver objects. Once done notifying and habving
     * resolved the relevant promises, it deletes the resolver objects from input map.
     * @param font A valid TQFont Object
     * @param resolverMap Map of TQFontIdentifiers and Promise resolver objects
     * @param proc A function to execute for triggering font download if any
     * @param isFontId boolean to denote if TQFont is referred by its fontId or postscriptName
     * @param error tqtypes.Error for notifying the exact error
     */
    private _matchAndResolvePromises = (
        font: TQFont,
        resolverMap: Map<tqtypes.TQFontIdentifer, any>,
        proc: undefined | (() => Promise<tqtypes.Error>),
        isFontId: boolean,
        error = new tqtypes.Error()
    ) => {
        // to handle dups
        const matchingEntries = isFontId
            ? Array.from(resolverMap).filter(entry => font.fontId == entry[0].fontId)
            : Array.from(resolverMap).filter(entry => font.postscriptName == entry[0].postscriptName);
        for (const [tqFontId, resolver] of matchingEntries) {
            if (proc != undefined) {
                proc().then(error => {
                    resolver({ error: error, font: font });
                });
            } else {
                resolver({ error: error, font: font });
            }
            resolverMap.delete(tqFontId);
        }
    };

    private _getCachedOrDownloadFoundFonts = async (
        font: TQFont,
        fontId: string,
        psName: string,
        fontsAndUnicodesMap: Map<string, number[] | string | undefined>,
        resolverMap: Map<tqtypes.TQFontIdentifer, (value: any) => void>
    ) => {
        const isFound = await this._scheduler.foundFontsHandler.populateFontBufferFromCache(font, false);
        const isFontId = fontId != "";
        if (isFound != true) {
            const unicodes = fontsAndUnicodesMap.get(isFontId ? font.fontId : font.postscriptName);
            const downloadCB = (fontInfo: TQFont, error: tqtypes.Error) => {
                this._matchAndResolvePromises(font, resolverMap, undefined, isFontId);
                return true;
            };
            const callbacks: tqtypes.GenericContext<tqtypes.InternalContext> = {
                root: {
                    downloadCB: downloadCB,
                    fontsList: [font.fontId],
                    fontsIds: [],
                    unicodesList: unicodes ? unicodes : "",
                    fontInfo: font,
                },
                retryCount: this._retryCount,
            };
            this._scheduler.scheduleTask(tqtypes.TaskType.FONT_DOWNLOAD, callbacks);
        } else {
            this._matchAndResolvePromises(font, resolverMap, undefined, isFontId);
        }
    };

    /** Safe Sync call for getting AF token. Fetches token only if required using TQWeb's last status */
    private async initTokenIfNeeded() {
        const status = this.getStatusCode();
        if (status == tqtypes.StatusCode.ERROR_OCCURED || status == tqtypes.StatusCode.UNINITIALIZED) {
            if (TQUtils.isValidAccessToken(this._scheduler.initParams.authParams.accessToken)) {
                this._scheduler._nonBlockingTokenCallStatus = true;
                await this.initToken();
            }
        }
    }

    /**
     * sync call to get status code
     * @return {tqtypes.StatusCode} as the status code
     */
    private getStatusCode() {
        return this._scheduler.status;
    }

    /* Not used currently */
    private async notifySystemFonts(tqFonts: TQFont[], downloadCallback: tqtypes.FontDownloadCallback) {
        for (const font of tqFonts) {
            // ...
            downloadCallback(font, new tqtypes.Error());
        }
    }

    /* Not used currently */
    private async notifyErrorSystemFonts(psnames: string[], downloadCallback: tqtypes.FontDownloadCallback) {
        for (const fontPsName of psnames) {
            // ...
            const singlefontInfo: TQFont = new TQFont(tqtypes.FontType.SYSTEM, "", "");
            singlefontInfo.postscriptName = fontPsName;
            downloadCallback(singlefontInfo, new tqtypes.Error(tqtypes.ErrorCode.FONT_NOT_FOUND, "Font not found in system"));
        }
    }

    /**
     * API call to get TQFont (with only metadata) for the given postscript names of Fonts.
     * @param {string[]} psNames Postscript names list
     * @param {string[]} fontIds Typekit ids list. One's own BYOF fonts can also be found using this
     * @return {Promise<TQFont[]>} Promise for the array of created TQFont(s) as a result. If a font was not found, it's typekitid would be empty
     */
    public async checkFonts(psNames: string[], fontIds: string[] = []) {
        return this._scheduler.foundFontsHandler.checkFonts(psNames, fontIds, await this.getUserEntitlement());
    }

    // Public methods

    /**
     * Constructor for TQWeb. Check TQWeb Class doc for the details
     * @param {TQInitParams} init Input Parameters, such as UserId, AccessToken, appId etc.
     * TQInitParams is dependent on further configurable parameters. Please check TQInitParams class further for details
     */
    constructor(init: TQInitParams) {
        this._scheduler = new TQTaskScheduler(init);
    }

    /**
     * Explicit call to start the TQWeb. This would fail work if access token / userId in set in tqtypes.AuthParams of TQInitParams is empty
     */
    public async start() {
        await this.initToken();
    }

    /**
     * API call to update the TQWeb language. If language is not set, TQWeb would pick the browser's language
     * @param {tqtypes.Language} lang Input language for which TQFontAggregtion needs to be fetched
     * @return {Promise<boolean>} Promise for the performed operation's status in boolean. Currently true always.
     */
    public async updateLanguage(lang: tqtypes.Language) {
        TQInitParams.setLanguage(lang);
        await this._scheduler.handleCacheOnInit();
        return true;
    }

    /**
     * A getter function for TQInitParams of TQWeb Object
     * @return {TQInitParams} TQInitParams of TQWeb Object.
     */
    public getInitParams() {
        return this._scheduler.initParams;
    }

    /**
     * API call to update the Font fetch strategy. This API is a no-op currenly, Please don't use it.
     * @param {tqtypes.FontFetchStrategy} strategy Font fetch strategy which can be system fonts only / Adobe fonts only / All.
     */
    public async updateFetchStrategy(strategy: tqtypes.FontFetchStrategy) {
        this._scheduler.initParams.fontFetchStrategy = strategy;
    }

    /**
     * API call to update the Auth params lazily or if required. If statusUpdateCallback under TQInitParams returns AUTH_FAILURE, clients must call it.
     * @param {string} userId UserId (AdobeGUID) for the user
     * @param {string} accessToken accessToken for the user
     * @return {Promise<boolean>} Promise for the performed operation's status in boolean.
     */
    public async updateAuthParams(userId: string, accessToken: string): Promise<boolean> {
        if (this._scheduler.initParams.authParams.accessToken == accessToken) {
            if (this.getStatusCode() == tqtypes.StatusCode.DONE) {
                return false;
            }
        }

        this._scheduler.initParams.authParams.userId = userId;
        this._scheduler.initParams.authParams.accessToken = accessToken;
        await this.initToken();
        return true;
    }

    /** API call to get status code of TQ
     * @return {Promise<tqtypes.StatusCode>} Promise for the tqtypes.StatusCode
     */
    public async getStatus() {
        return this.getStatusCode();
    }

    /**
     * It returns the entitlement of the user, which is either FULL or TRIAL.
     * @returns The entitlement of the user, if fetched or cached else undefined
     */
    public async getUserEntitlement(): Promise<tqtypes.Entitlement | undefined> {
        const entitlement = await this._scheduler.fetchCachedEntitlement();
        if (entitlement == tqtypes.Entitlement.FULL || entitlement == tqtypes.Entitlement.TRIAL || entitlement == tqtypes.Entitlement.ANONYMOUS) {
            return entitlement;
        }
        await this.initTokenIfNeeded();

        const status = await this.getStatusCode();
        if (status == tqtypes.StatusCode.ERROR_OCCURED) {
            return undefined;
        }
        return this._scheduler.obtainedTokenParams.entitlement;
    }

    /**
     * API call to get fontBuffer of an already available TQFont Object that doesn't have buffer. This might be the a TQFont with just metadata that was obtained from TQ via checkFonts API.
     * After completion, Font buffer would be updated in the same font object.
     * @param {TQFont} font TQFont for which buffer should be fetched.
     * @param { number[] | string} unicodesToFetch In case partial font subset is needed, Unicodes array of or plain text.
     * @return {Promise<tqtypes.Error>} Promise for the tqtypes.Error about Error occurred if any.
     */
    public async findFont(font: TQFont, unicodesToFetch: number[] | string): Promise<tqtypes.Error> {
        // Please supply either subset string or unicodes for fetching a subset font
        const cb: any = null;
        if (typeof unicodesToFetch === "string") {
            unicodesToFetch = unicodesToFetch.split("").map(x => x.charCodeAt(0));
        }
        unicodesToFetch = Array.from([...new Set(unicodesToFetch)]);
        const callbacks: tqtypes.GenericContext<tqtypes.InternalContext> = {
            root: {
                downloadCB: cb,
                fontsList: [font.fontId],
                fontsIds: [],
                fontInfo: font,
                unicodesList: unicodesToFetch,
            },
            retryCount: this._retryCount,
        };
        const isFound = await this._scheduler.foundFontsHandler.populateFontBufferFromCache(font, false);
        if (isFound) {
            return new Promise((resolve, reject) => {
                resolve(new tqtypes.Error());
            });
        }
        return await this._scheduler.scheduleTask(tqtypes.TaskType.FONT_DOWNLOAD, callbacks);
    }

    /**
     * API call to get metadata and optionally fontBuffer of font wrapped inside a TQFont Object from a given postscriptName or fontId
     * After completion, if requested, Font buffer would be provided in the same font object.
     * @param {Set<tqtypes.TQFontIdentifer>} fontsAndUnicodes Set of Font Identifiers, a type containing Postscript name or fontId, and optionally unicodes
     * @param {tqtypes.OperationMode} operationMode Flag to control if fontbuffer + metadata OR just metadata should be fetched.
     * @return {Promise<Map<tqtypes.TQFontIdentifer, Promise<tqtypes.FindFontPromise>>>} This would return promise of a Map { input TQFontIdentifer, their promises}.
     * Each FindFontPromise promise related to TQFontIdentifer would be then resolved to get the font or error, if any.
     */
    public async findFontsAsync(
        fontsAndUnicodes: Set<tqtypes.TQFontIdentifer>,
        operationMode: tqtypes.OperationMode = tqtypes.OperationMode.DATA
    ): Promise<Map<tqtypes.TQFontIdentifer, Promise<tqtypes.FindFontPromise>>> {
        // If All or system strategy, then only consult system fonts
        const strategy: tqtypes.FontFetchStrategy = this._scheduler.initParams.fontFetchStrategy;
        let psNames: string[] = [];
        const fontIds: string[] = [];
        for (const fontAndUnicode of fontsAndUnicodes) {
            if (fontAndUnicode.fontId && fontAndUnicode.fontId != "") {
                fontIds.push(fontAndUnicode.fontId);
            } else if (fontAndUnicode.postscriptName && fontAndUnicode.postscriptName != "") {
                psNames.push(fontAndUnicode.postscriptName);
            }
        }
        const promisesMap = new Map<tqtypes.TQFontIdentifer, Promise<tqtypes.FindFontPromise>>();
        const resolverMap = new Map<tqtypes.TQFontIdentifer, (value: any) => void>();

        for (const entry of fontsAndUnicodes) {
            promisesMap.set(
                entry,
                new Promise((resolve, reject) => {
                    resolverMap.set(entry, resolve);
                })
            );
        }

        if (strategy == tqtypes.FontFetchStrategy.ALL || strategy == tqtypes.FontFetchStrategy.SYSTEM) {
            // Filter out fonts which fonts found are system
            const { remainingFonts, foundSystemFonts } = await TQUtils.findSystemFonts(psNames, false);
            for (const font of foundSystemFonts) {
                this._matchAndResolvePromises(font, resolverMap, undefined, false);
            }
            psNames = remainingFonts;

            if (psNames.length == 0 && fontIds.length == 0) {
                return promisesMap;
            }

            if (strategy == tqtypes.FontFetchStrategy.SYSTEM) {
                const err = new tqtypes.Error(tqtypes.ErrorCode.FONT_NOT_FOUND, "Font not found in system");
                for (const fontId of fontIds) {
                    const singlefontInfo: TQFont = new TQFont(tqtypes.FontType.SYSTEM, "", fontId);
                    this._matchAndResolvePromises(singlefontInfo, resolverMap, undefined, true, err);
                }
                for (const fontPsName of psNames) {
                    const singlefontInfo: TQFont = new TQFont(tqtypes.FontType.SYSTEM, fontPsName, "");
                    this._matchAndResolvePromises(singlefontInfo, resolverMap, undefined, false, err);
                }
                return promisesMap;
            }
        }

        if (this._scheduler._nonBlockingTokenCallStatus != true) {
            this.initTokenIfNeeded();
            this._scheduler._nonBlockingTokenCallStatus = true;
        }

        const ret = await this.checkFonts(psNames, fontIds);
        const psNameArray = [...ret.psNames.values()];
        const fontIdArray = [...ret.fontIds.values()];

        const notFoundPsNames = psNameArray.filter(font => font.fontId == "");
        const notFoundFontIds = fontIdArray.filter(font => font.postscriptName == "");
        const psNameFoundArray = psNameArray.filter(font => font.fontId != "");
        const fontIdFoundArray = fontIdArray.filter(font => font.postscriptName != "");

        let index = 0;
        for (; index < fontIdFoundArray.length; index++) {
            const font = fontIdFoundArray[index];
            const match = [...fontsAndUnicodes].filter(entry => entry.fontId && entry.fontId == font.fontId);
            if (match.length && match[0].postscriptName && font.postscriptName != match[0].postscriptName) {
                // Perhaps a variable font? Needs correction in metadata
                const reMatch = await this.checkFonts([match[0].postscriptName], []);
                const fontIfExists = reMatch.psNames.get(match[0].postscriptName);
                if (fontIfExists && fontIfExists.fontId == font.fontId && fontIfExists.postscriptName == match[0].postscriptName) {
                    fontIdFoundArray[index] = fontIfExists;
                }
            }
        }

        if (notFoundPsNames.length || notFoundFontIds.length) {
            const err = new tqtypes.Error(tqtypes.ErrorCode.FONT_NOT_FOUND, "Font not found");

            for (const font of notFoundPsNames) {
                this._matchAndResolvePromises(font, resolverMap, undefined, false, err);
            }
            for (const font of notFoundFontIds) {
                this._matchAndResolvePromises(font, resolverMap, undefined, true, err);
            }
        }

        // Filter rest of the fonts
        if (operationMode == tqtypes.OperationMode.CHECK) {
            for (const font of psNameFoundArray) {
                this._matchAndResolvePromises(font, resolverMap, undefined, false);
            }
            for (const font of fontIdFoundArray) {
                this._matchAndResolvePromises(font, resolverMap, undefined, true);
            }
            return promisesMap;
        }

        // Font buffer needs to be fetched.
        fontsAndUnicodes.forEach(entry => {
            if (entry.unicodes && typeof entry.unicodes === "string") {
                entry.unicodes = entry.unicodes.split("").map(x => x.charCodeAt(0));
                entry.unicodes = Array.from([...new Set(entry.unicodes)]);
            }
        });

        const fontsAndUnicodesMap: Map<string, number[] | string | undefined> = new Map();
        for (const fontsAndUnicode of fontsAndUnicodes) {
            if (fontsAndUnicode.fontId && fontsAndUnicode.fontId != "") {
                fontsAndUnicodesMap.set(fontsAndUnicode.fontId, fontsAndUnicode.unicodes);
            } else if (fontsAndUnicode.postscriptName && fontsAndUnicode.postscriptName != "") {
                fontsAndUnicodesMap.set(fontsAndUnicode.postscriptName, fontsAndUnicode.unicodes);
            }
        }

        for (const font of psNameFoundArray) {
            this._getCachedOrDownloadFoundFonts(font, "", font.postscriptName, fontsAndUnicodesMap, resolverMap);
        }
        for (const font of fontIdFoundArray) {
            this._getCachedOrDownloadFoundFonts(font, font.fontId, font.postscriptName, fontsAndUnicodesMap, resolverMap);
        }
        return promisesMap;
    }

    /**
     * The API call to get metadata or font files of any shared document's contaning AF/BYOF fonts.
     * @param {Set<tqtypes.TQFontIdentifer>} fontsAndUnicodes Set of Font Identifiers, a type containing Postscript name or fontId, and optionally unicodes
     * @param {tqtypes.OperationMode} operationMode Flag to control if fontbuffer + metadata OR just metadata should be fetched.
     * @param {string} fontAccessToken An expiring JWT which should be passed in the HTTP headers for font access auth requests.
     * @param {string} documentId The document identifier used to generate the access token.
     * @return {Promise<Map<tqtypes.TQFontIdentifer, Promise<tqtypes.FindFontPromise>>>} This would return promise of a Map { input TQFontIdentifer, their promises}.
     */
    public async findFontsUsingCollabAuthAPI(
        fontsAndUnicodes: Set<tqtypes.TQFontIdentifer>,
        operationMode: tqtypes.OperationMode = tqtypes.OperationMode.DATA,
        fontAccessToken: string,
        documentId: string
    ): Promise<Map<tqtypes.TQFontIdentifer, Promise<tqtypes.FindFontPromise>>> {
        const psNames: string[] = [];
        const tkIds: string[] = [];
        const byofIds: string[] = [];

        const promisesMap = new Map<tqtypes.TQFontIdentifer, Promise<tqtypes.FindFontPromise>>();
        const resolverMap = new Map<tqtypes.TQFontIdentifer, (value: any) => void>();

        for (const entry of fontsAndUnicodes) {
            promisesMap.set(
                entry,
                new Promise((resolve, reject) => {
                    resolverMap.set(entry, resolve);
                })
            );
        }
        for (const fontAndUnicode of fontsAndUnicodes) {
            if (fontAndUnicode.fontId && fontAndUnicode.fontId != "") {
                if (fontAndUnicode.fontId.substring(0, 4) == "TkD-") {
                    tkIds.push(fontAndUnicode.fontId);
                } else {
                    byofIds.push(fontAndUnicode.fontId);
                }
            } else if (fontAndUnicode.postscriptName && fontAndUnicode.postscriptName != "") {
                psNames.push(fontAndUnicode.postscriptName);
            }
        }
        if (this._scheduler._nonBlockingTokenCallStatus != true) {
            this.initTokenIfNeeded();
            this._scheduler._nonBlockingTokenCallStatus = true;
        }
        const ret = await this.checkFonts(psNames, tkIds); // This is required so that we understand the permissions of found fonts

        for (const byofId of byofIds) {
            const fontIfExists = await this._scheduler.getFontFromLocalInfo("", byofId);
            if (fontIfExists && fontIfExists.postscriptName != "") {
                ret.fontIds.set(byofId, fontIfExists);
            } else {
                ret.fontIds.set(byofId, new TQFont(tqtypes.FontType.BYOF, "", byofId));
            }
        }

        const psNameArray = [...ret.psNames.values()];
        const fontIdArray = [...ret.fontIds.values()];

        const notFoundPsNames = new Map([...ret.psNames].filter(([key, font]) => font.fontId == ""));
        const notFoundFontIds = new Map([...ret.fontIds].filter(([key, font]) => font.postscriptName == ""));

        const psNameFoundArray = psNameArray.filter(font => font.fontId != "");
        const fontIdFoundArray = fontIdArray.filter(font => font.postscriptName != "");

        let index = 0;
        for (; index < fontIdFoundArray.length; index++) {
            const font = fontIdFoundArray[index];
            const match = [...fontsAndUnicodes].filter(entry => entry.fontId && entry.fontId == font.fontId);
            if (match.length && match[0].postscriptName && font.postscriptName != match[0].postscriptName) {
                // Perhaps a variable font? Needs correction in metadata
                const reMatch = await this.checkFonts([match[0].postscriptName], []);
                const fontIfExists = reMatch.psNames.get(match[0].postscriptName);
                if (fontIfExists && fontIfExists.fontId == font.fontId && fontIfExists.postscriptName == match[0].postscriptName) {
                    fontIdFoundArray[index] = fontIfExists;
                }
            }
        }

        let results: any = new Map();
        const collabFontsHandler = new TQCollabFontsHandler(this._scheduler);
        if (notFoundPsNames.size || notFoundFontIds.size) {
            results = await collabFontsHandler.getFontMetadataUsingCollabAuthAPI(notFoundPsNames, notFoundFontIds, fontAccessToken, documentId);
        }

        // Font buffer needs to be fetched.
        fontsAndUnicodes.forEach(entry => {
            if (entry.unicodes && typeof entry.unicodes === "string") {
                entry.unicodes = entry.unicodes.split("").map(x => x.charCodeAt(0));
                entry.unicodes = Array.from([...new Set(entry.unicodes)]);
            }
        });

        const fontsAndUnicodesMap: Map<string, number[] | string | undefined> = new Map();
        for (const fontsAndUnicode of fontsAndUnicodes) {
            if (fontsAndUnicode.fontId && fontsAndUnicode.fontId != "") {
                fontsAndUnicodesMap.set(fontsAndUnicode.fontId, fontsAndUnicode.unicodes);
            } else if (fontsAndUnicode.postscriptName && fontsAndUnicode.postscriptName != "") {
                fontsAndUnicodesMap.set(fontsAndUnicode.postscriptName, fontsAndUnicode.unicodes);
            }
        }

        const resultHandler = async (psName: string, fontId: string, type: tqtypes.FontType) => {
            let error = new tqtypes.Error(tqtypes.ErrorCode.FONT_NOT_FOUND);
            let entryExists: tqtypes.FontCollaborationInternalContext | undefined = undefined;
            if (fontId != "") {
                entryExists = results.get(fontId);
            } else if (psName != "") {
                entryExists = results.get(psName);
            }
            let font = new TQFont(type, psName, fontId);
            if (entryExists) {
                font = entryExists.fontInfo;
                error = entryExists.error;
                if (operationMode == tqtypes.OperationMode.DATA && error.errCode == tqtypes.ErrorCode.NONE) {
                    const isFound = await this._scheduler.foundFontsHandler.populateFontBufferFromCache(font, false);
                    if (isFound != true) {
                        error = await collabFontsHandler.downloadCollabFont(entryExists);
                    }
                }
            }
            this._matchAndResolvePromises(font, resolverMap, undefined, fontId != "", error);
            return true;
        };

        for (const font of psNameFoundArray) {
            this._getCachedOrDownloadFoundFonts(font, "", font.postscriptName, fontsAndUnicodesMap, resolverMap);
        }
        for (const font of fontIdFoundArray) {
            this._getCachedOrDownloadFoundFonts(font, font.fontId, "", fontsAndUnicodesMap, resolverMap);
        }

        for (const font of notFoundPsNames.values()) {
            resultHandler(font.postscriptName, "", tqtypes.FontType.DEFAULT);
        }
        for (const font of notFoundFontIds.values()) {
            if (font.fontId.substring(0, 4) == "TkD-") {
                resultHandler("", font.fontId, tqtypes.FontType.DEFAULT);
            } else {
                resultHandler("", font.fontId, tqtypes.FontType.BYOF);
            }
        }
        return promisesMap;
    }

    /**
     * API call to get TQFont( fontBuffer + metadata OR just metadata) objects for the given postscript names of Fonts.
     * If operationMode is tqtypes.OperationMode.CHECK means => just fetch the metadata from AF / Cache. (Default)
     * If operationMode is tqtypes.OperationMode.DATA means => fetch the metadata + font buffer from AF / Disk Cache.
     * After completion, proper TQFont(s) would be provided in the downloadCallback.
     * @param {string[]} fonts Postscript names list
     * @param {tqtypes.OperationMode} operationMode Flag to control if fontbuffer + metadata OR just metadata should be fetched.
     * @param { number[] | string} unicodesToFetch In case partial font subset is needed, Unicodes array of or plain text.
     * @param {tqtypes.FontDownloadCallback} downloadCallback The callback to be called for as soon as the results are available.
     * This can be called multiple time depending upon the size of the provided Postscript names list
     */
    public async findFonts(
        fonts: string[],
        operationMode: tqtypes.OperationMode = tqtypes.OperationMode.CHECK,
        unicodesToFetch: number[] | string, // Please supply either subset string or unicodes for fetching a subset font
        downloadCallback: tqtypes.FontDownloadCallback
    ) {
        if (typeof unicodesToFetch === "string") {
            unicodesToFetch = unicodesToFetch.split("").map(x => x.charCodeAt(0));
        }
        unicodesToFetch = Array.from([...new Set(unicodesToFetch)]);
        const perChunk = 30; // max 30 items per chunk

        fonts = fonts.filter(entry => entry != undefined && entry != null);

        // If All or system strategy, then only consult system fonts
        const strategy: tqtypes.FontFetchStrategy = this._scheduler.initParams.fontFetchStrategy;
        if (strategy == tqtypes.FontFetchStrategy.ALL || strategy == tqtypes.FontFetchStrategy.SYSTEM) {
            // Filter out fonts which fonts found are system
            const { remainingFonts, foundSystemFonts } = await TQUtils.findSystemFonts(fonts, false);
            fonts = remainingFonts;

            this.notifySystemFonts(foundSystemFonts, downloadCallback);
            if (fonts.length == 0) {
                return;
            }
            if (strategy == tqtypes.FontFetchStrategy.SYSTEM && fonts.length) {
                // Notify error for remaining fonts
                this.notifyErrorSystemFonts(fonts, downloadCallback);
                return;
            }
        } else {
            // console.log("Not checking system fonts due to selected preference");
        }

        if (this._scheduler._nonBlockingTokenCallStatus != true) {
            this.initTokenIfNeeded();
            this._scheduler._nonBlockingTokenCallStatus = true;
        }
        let foundFonts: TQFont[] = Array.from((await this.checkFonts(fonts)).psNames.values());
        foundFonts = foundFonts.filter(font => {
            if (font.fontId == "") {
                const matchingEntries = fonts.filter(psName => font.postscriptName == psName); // to handle dups
                for (const entry of matchingEntries) {
                    (async () => {
                        downloadCallback(font, new tqtypes.Error(tqtypes.ErrorCode.FONT_NOT_FOUND, "Font not found"));
                    })().catch(err => {
                        // ..
                    });
                }
                return false;
            }
            return true;
        });

        // follow the regular path for rest of the fonts with a max batch of 30
        for (const font of foundFonts) {
            const matchingEntries = fonts.filter(psName => font.postscriptName == psName); // to handle dups
            for (const entry of matchingEntries) {
                if (operationMode == tqtypes.OperationMode.CHECK) {
                    downloadCallback(font, new tqtypes.Error(tqtypes.ErrorCode.NONE, ""));
                } else {
                    const isFound = await this._scheduler.foundFontsHandler.populateFontBufferFromCache(font, false);
                    if (isFound) {
                        downloadCallback(font, new tqtypes.Error(tqtypes.ErrorCode.NONE, ""));
                        continue;
                    }
                    const callbacks: tqtypes.GenericContext<tqtypes.InternalContext> = {
                        root: {
                            downloadCB: downloadCallback,
                            fontsList: [font.fontId],
                            fontsIds: [],
                            unicodesList: unicodesToFetch,
                            fontInfo: font,
                        },
                        retryCount: this._retryCount,
                    };
                    this._scheduler.scheduleTask(tqtypes.TaskType.FONT_DOWNLOAD, callbacks);
                }
            }
        }
    }

    private _fetchFreshCollection(taskType: tqtypes.TaskType) {
        const aggregtion = new TQFontAggregation();
        const callback: tqtypes.GenericContext<tqtypes.AggregationContext> = {
            root: {
                page: 1,
                perPage: 100,
                isRequested: true,
            },
            retryCount: 2,
        };

        const response = {
            aggregation: new TQFontAggregation(),
            error: new tqtypes.Error(),
        };
        return this._scheduler.scheduleTask(taskType, callback) as Promise<typeof response>;
    }

    /**
     * API call to get TQFontAggregation(s) so that the font browsing menu can be displayed.
     * @param {tqtypes.FontType} type This can be tqtypes.FontType.ACTIVATED or BYOF or DEFAULT. For DEFAULT, familiy slugs should be specified in next parameter.
     * This can also be APPLICATION but for APPLICATION you must to specify in init params what app specific fonts do you wish to refer.
     * See more in tqtypes.TQInitParams.applicationFontsCollectionType
     * @param {string[]} families Family slugs list
     * @return {Promise<TQFontAggregation, tqtypes.Error>} where {Promise<aggregation: TQFontAggregation, error:tqtypes.Error>} is the result
     * TQFontAggregation is a collection of TQFontFamily(s) and TQFontFamily is an ordered group of TQFont(s). These must be enumerated by getEntries(..) function
     */
    public async getFontCollection(type: tqtypes.FontType, families: string[] = []) {
        const aggregtion = new TQFontAggregation();
        const callback: tqtypes.GenericContext<tqtypes.AggregationContext> = {
            root: {
                page: 1,
                perPage: 100,
                isRequested: true,
            },
            retryCount: 2,
        };

        const response = {
            aggregation: new TQFontAggregation(),
            error: new tqtypes.Error(),
        };

        if (this._scheduler._nonBlockingTokenCallStatus != true) {
            this.initTokenIfNeeded();
            this._scheduler._nonBlockingTokenCallStatus = true;
        }

        const start = performance.now();
        if (type == tqtypes.FontType.ACTIVATED) {
            if (TQUtils.isValidAccessToken(this._scheduler.initParams.authParams.accessToken) == false) {
                return response;
            }
            const task = () => {
                return this._scheduler.scheduleTask(tqtypes.TaskType.ACTIVATED_FONTS_COUNT, callback) as Promise<typeof response>;
            };
            const ret = await this._scheduler.getCachedActivated("", aggregtion, true);
            if (ret) {
                callback.root.isRequested = false;
                setTimeout(task, 5000);
                TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `cached ACTIVATED done. Took ${Math.round(performance.now() - start)} milliseconds.`);
                return { aggregation: aggregtion, error: new tqtypes.Error() };
            }
            return task();
        }

        if (type == tqtypes.FontType.BYOF) {
            if (this._scheduler.performByofValidationScope() == false) {
                return response;
            }
            const task = () => {
                return this._scheduler.scheduleTask(tqtypes.TaskType.BYOF_COUNT, callback) as Promise<typeof response>;
            };
            const ret = await this._scheduler.getCachedByof("", aggregtion, true);
            if (ret) {
                callback.root.isRequested = false;
                setTimeout(task, 5000);
                TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `cached BYOF done. Took ${Math.round(performance.now() - start)} milliseconds.`);
                return { aggregation: aggregtion, error: new tqtypes.Error() };
            }
            return task();
        }

        if (type == tqtypes.FontType.DEMO) {
            return this._scheduler.getDemoFontsCollection() as Promise<typeof response>;
        }

        if (type == tqtypes.FontType.APPLICATION) {
            return this._scheduler.fetchAndprocessCustomCatalog() as Promise<typeof response>;
        }

        if (type == tqtypes.FontType.DEFAULT) {
            const scheduler = this._scheduler;
            const resolvePromise = (slugs: string[]) =>
                new Promise((resolve, reject) => {
                    scheduler.getFamiliesFromSlugsOnceAvailable(
                        (response: any) => {
                            TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `cached DEFAULT done. Took ${Math.round(performance.now() - start)} milliseconds.`);
                            resolve(response);
                            return true;
                        },
                        slugs,
                        true
                    );
                });
            return resolvePromise(families) as Promise<typeof response>;
        }
        return Promise.resolve(response);
    }

    /**
     * API call that searches and works as an async generator for family w.r.t search results
     * @param {string} searchKey This searchKey can be name, family or even style name of the font. Can be localized or plain as well.
     * @return {AsyncGenerator<TQFontFamily, void, unknown>} This AsyncGenerator can yield result if available or none if no relevant results found.
     * Each TQFontFamily is an ordered group of TQFont(s). These must be enumerated by getEntries(.. function
     */
    public async *searchFonts(searchKey: string) {
        searchKey = searchKey.trim().replace(/  +/g, " ");
        let dbAggregation = new TQFontAggregation();
        if (searchKey != "") {
            const searchItems = searchKey.split(" ");
            if (dbAggregation.getEntries().size == 0) {
                await this._scheduler.dbWrapper.getAggregationFromFuzzySearch(searchItems, dbAggregation, this._scheduler.initParams.appEntitlement, this._scheduler.obtainedTokenParams.entitlement); // Try with each keyword
            }
            const cachedFilteredFonts = [...this._scheduler.cachedFonts().values()].filter(entry => entry.isBrowsable == true);
            const cachedFontsAggregation = TQUtils.getAggregationFromFonts(cachedFilteredFonts, searchItems); // Filter cached Fonts with this info

            let resultLength = 0;
            for (const family of cachedFontsAggregation.getRawMap()) {
                resultLength = resultLength + family[1].getRawUnorderedMap().size;
            }

            if (dbAggregation.getEntries().size == 0 && resultLength < 5) {
                // No DB results
                const slugNames = await this._scheduler.getSlugNames(searchItems.slice(0, 1));
                const res = await this._scheduler._fetchDefaultFamiliesData(Array.from(slugNames), false, searchItems);
                dbAggregation = res.aggregation; // Pass all search results, need to verify

                if (dbAggregation.getEntries().size == 0) {
                    // First server cached BYOF fonts
                    for (const familyEntry of cachedFontsAggregation.getRawMap()) {
                        yield familyEntry[1];
                    }
                    const slugPromises = await this._scheduler.serverQueryGetSlugPromises(slugNames);
                    for await (const familyInfos of slugPromises) {
                        const fontsArray: JSON[] = familyInfos["fonts"];
                        const displayFontPsName: string = familyInfos["display_font"]["postscript_name"];

                        let searchAggregation = new TQFontAggregation();
                        searchAggregation = TQUtils.getFamiliesFromFontsArrayJSON(
                            fontsArray,
                            tqtypes.FontType.DEFAULT,
                            this._scheduler.obtainedTokenParams.entitlement,
                            searchAggregation,
                            displayFontPsName,
                            null,
                            this._scheduler.initParams.appEntitlement,
                            false,
                            []
                        ); // No Pass any search results, no need to verify

                        // yield results one by one
                        for (const familyEntry of searchAggregation.getRawMap()) {
                            yield familyEntry[1];
                        }
                    }
                    return;
                }
            }
            // Yield all DB results
            dbAggregation.merge(cachedFontsAggregation);
            for (const familyEntry of dbAggregation.getRawMap()) {
                yield familyEntry[1];
            }
        }
    }

    /**
     * This API would update the recents for the user in case they should be updated, based upon the tqtypes.RecentsUpdatePreference defined in tqtypes.InitParams.
     * Hence. a recentFontsUpdateCallback specified under TQInitParams may get called so that UI can update recents.
     * This would also report the font usage to Adobe Fonts and auto-activate the fonts as per supplied params to OS/CC level.
     * @param {tqtypes.TQFontIdentifer[]} fonts For which usage is being reported. No need to call for BYOF fonts, that would be no-op and ignored by TQWeb.
     * @param {string} docId string Identifier for the document.
     * @param {tqtypes.FontUsageOperation} operation This can be OPEN / APPLY depending upon the font usage
     * @param {boolean} autoActivate If fonts should be marked as activated and sync'ed to all the user devices
     * @param {tqtypes.InstallState} installState This can be OS / CC, OS means font would get installed for all third party usage, CC if only for Adobe apps
     * @param {boolean} readonly A flag indicating the font was used in a readonly context. This true option negates the "activate" and "install_state" options.
     * Sucn events will not be used in royalty payment accounting calculations. Default value is false
     */
    public async reportUseFonts(fonts: tqtypes.TQFontIdentifer[], docId: string, operation: tqtypes.FontUsageOperation, autoActivate = true, installState = tqtypes.InstallState.CC, readonly = false) {
        fonts = fonts.filter(elem => {
            return elem != null && (elem.fontId != "" || elem.postscriptName != "");
        });

        const psNames: string[] = [];
        const fontIds: string[] = [];
        for (const font of fonts) {
            if (font.fontId && font.fontId != "") {
                if (font.fontId.substring(0, 4) == "TkD-") {
                    fontIds.push(font.fontId);
                } // else ignore
            } else if (font.postscriptName && font.postscriptName != "") {
                psNames.push(font.postscriptName);
            }
        }

        const tkIDsList: string[] = [];
        const ret = await this.checkFonts(psNames, fontIds);
        let filtered: TQFont[] = [...ret.fontIds.values(), ...ret.psNames.values()].filter(entry => entry.postscriptName != "");

        switch (this._scheduler.initParams.recentsUpdatePreference) {
            case tqtypes.RecentsUpdatePreference.ONLY_APPLY:
                {
                    if (operation == tqtypes.FontUsageOperation.APPLY) {
                        this._scheduler.updateRecentsListAndBroadcastMessage(filtered);
                    }
                }
                break;

            case tqtypes.RecentsUpdatePreference.BOTH_OPEN_AND_APPLY:
                {
                    this._scheduler.updateRecentsListAndBroadcastMessage(filtered);
                }
                break;

            case tqtypes.RecentsUpdatePreference.ONLY_OPEN:
                {
                    if (operation == tqtypes.FontUsageOperation.OPEN) {
                        this._scheduler.updateRecentsListAndBroadcastMessage(filtered);
                    }
                }
                break;
        }

        filtered = filtered.filter(entry => entry.fontId != "");

        for (const font of filtered) {
            if (font && font.fontId != "" && font.type != tqtypes.FontType.BYOF && font.type != tqtypes.FontType.APPLICATION) {
                tkIDsList.push(font.fontId);
            }
        }

        if (filtered.length) {
            const callbacks: tqtypes.GenericContext<tqtypes.UsageInternalContext> = {
                root: { documentId: docId, fontsList: tkIDsList, operation: operation, autoActivate: autoActivate, installState: installState, readonly: readonly },
                retryCount: this._retryCount,
            };
            this._scheduler.scheduleTask(tqtypes.TaskType.USAGE_REPORT, callbacks);
        }
    }

    /**
     * This API checks with AdobeFonts if required and gets all the supported languages localized names.
     * @return {Promise<Map<tqtypes.Language, string>, tqtypes.error>} where {Promise<languageMap: Map<tqtypes.Language, string>, error:tqtypes.Error>}
     * Map of TQ languages and their localized names along with error if any.
     */
    public async getSupportedLanguages(): Promise<{
        languageMap: Map<tqtypes.Language, string>;
        error: tqtypes.Error;
    }> {
        return await this._scheduler.getLangNames();
    }

    /**
     * This API would fetch the TQFontAggregtion (collection of TQFontFamily) for the given language
     * @param {tqtypes.Language} lang Input language for which TQFontAggregtion needs to be fetched
     * @return {Promise<TQFontAggregation>} Promise for the TQFontAggregtion.
     */
    public async getFontsByLanguage(lang: tqtypes.Language) {
        const scheduler = this._scheduler;
        const resolvePromise = (lang: tqtypes.Language) =>
            new Promise((resolve, reject) => {
                scheduler.getFontsByLanguageOnceAvailable((response: TQFontAggregation) => {
                    resolve(response);
                    return true;
                }, lang);
            });
        const aggregation = (await resolvePromise(lang)) as TQFontAggregation;
        return aggregation;
    }

    /**
     * This API would fetch the array of sibling TQFonts that are not already included in the TQFontFamily to be entered.
     * @param {TQFontFamily} tqFamily Input TQFontFamily whose missing fonts needs to be returned.
     * @return {Promise<TQFont[]>} Promise for the full TQFont array matching postscript name of the font.
     */
    public async getMoreFonts(tqFamily: TQFontFamily) {
        const scheduler = this._scheduler;
        const resolvePromise = (tqFamily: TQFontFamily) =>
            new Promise((resolve, reject) => {
                scheduler.getMoreFontsOnceAvailable((response: TQFont[]) => {
                    resolve(response);
                    return true;
                }, tqFamily);
            });
        const fonts = (await resolvePromise(tqFamily)) as TQFont[];
        return fonts;
    }

    /**
     * This API would fetch the tags based classification of the Adobe Fonts.
     * @return {Promise<Map<string, tqtypes.VariationTag>, tqtypes.error>} where {Promise<tags: Map<string,tqtypes.VariationTag>, error:tqtypes.error>} Map of TQ languages and their localized names
     */
    public async getVariationTags() {
        const scheduler = this._scheduler;
        const resolvePromise = () =>
            new Promise((resolve, reject) => {
                scheduler.getTagCatalogsOnceAvailable((tags: Map<string, tqtypes.VariationTag>, error: tqtypes.Error) => {
                    resolve({ tags: tags, error: error });
                    return true;
                });
            });
        return (await resolvePromise()) as {
            tags: Map<string, tqtypes.VariationTag>;
            error: tqtypes.Error;
        };
    }

    /**
     * This API can toggle the selections polling timer. If pauseRequested is true,
     * @param {boolean} pauseRequested If true, polling timers would be paused, else resumed.
     */
    public async toggleSelectionsPolling(pauseRequested: boolean) {
        await this._scheduler.togglePollTimers(pauseRequested);
    }

    /**
     * This API would fetch the full TQFontFamily from the given font Id or postscript name.
     * @param {tqtypes.TQFontIdentifer} tqFontId FontId or Postscript name of the font, whose full TQFontFamily we wish to fetch.
     * @return {Promise<TQFontFamily>} Promise for the full TQFontFamily that has postscript name of the font as a child.
     */
    public async getFamilyFromFontIdentifier(tqFontId: tqtypes.TQFontIdentifer) {
        const scheduler = this._scheduler;
        const resolvePromise = (tqFontId: tqtypes.TQFontIdentifer) =>
            new Promise((resolve, reject) => {
                scheduler.getFamilyOnceAvailable((response: TQFontFamily) => {
                    resolve(response);
                    return true;
                }, tqFontId);
            });
        return (await resolvePromise(tqFontId)) as TQFontFamily;
    }

    /**
     * This API would prevalidate a potential font file object as BYOF.
     * @param {File} file A file object to upload as BYOF font
     * @return { Promise<TQByofFileValidatorResult } where TQByofFileValidatorResult is the object that represents validation status of the font.
     * This contains TQFont object, error types and error if any. In case isFatalError is true, BYOF validation is considered failed.
     */
    public async prevalidateByofFont(file: File): Promise<TQByofFileValidatorResult> {
        return this._scheduler.prevalidateByofFont(file);
    }

    /**
     * This API would upload a font file object as BYOF. Please pass the the file object only if no error was observed as per prevalidateByofFont function.
     * @param {File} file A file object to upload as BYOF font
     * @return {Promise<Map<boolean, tqtypes.Error>, tqtypes.error>} where {Promise<status: boolean, error:tqtypes.error>} Promise for the status, error.
     */
    public async uploadByofFont(file: File): Promise<{ status: boolean; error: tqtypes.Error }> {
        return await this._scheduler.uploadByofFont(file);
        // this._scheduler.scheduleTask(tqtypes.TaskType.ANS_SHORT_POLL, null);
    }

    /**
     * This function would generate a custom OTVar instance postscript name and return it.
     * @param axisValues
     */
    public async generateCustomOTVarInstance(postscriptName: string, axisValues: Map<string, number>): Promise<TQFont> {
        const scheduler = this._scheduler;
        const resolvePromise = (psName: string) =>
            new Promise((resolve, reject) => {
                this.findFonts([postscriptName], tqtypes.OperationMode.DATA, [], (fontInfo: TQFont, error: tqtypes.Error) => {
                    resolve(fontInfo);
                    return true;
                });
            });
        const tqFont = (await resolvePromise(postscriptName)) as TQFont;
        if (tqFont.fontId != "") {
            tqFont.varAxisValues = new Map([...axisValues]);
            const generatedName = tqFont.generateCustomOTVarInstance(axisValues);
            if (generatedName) {
                tqFont.fontName = tqFont.variableFamilyPrefix + " Custom";
                tqFont.postscriptName = generatedName;
            }
        }
        return tqFont;
    }

    /**
     * This API checks if the user has access to upload a BYOF file against the provided Access Token in initParams
     * @return {Promise<tqtypes.Error, boolean >} where Promise<{ error: tqtypes.Error, hasAccess: boolean } is Promise for the status, error.
     */
    public async hasUserAccessForByofUpload(): Promise<{ error: tqtypes.Error; hasAccess: boolean }> {
        return await this._scheduler.hasUserAccessForByofUpload();
    }

    /**
     * This API allows a client application to delete a user's authorization to a given BYOF font.
     * @param {string} checksum - identifer of the BYOF font, obtained by typekitId getter of the TQFont
     * @return {Promise<tqtypes.Error, boolean >} where Promise<{ error: tqtypes.Error, hasAccess: boolean } is Promise for the status, error.
     */
    public async deleteByofAuth(checksum: string): Promise<{ error: tqtypes.Error; status: boolean }> {
        return await this._scheduler.deleteByofAuth([checksum]);
    }
}
